import { useState } from "react";
import { CalcLayout, NumField, Stat } from "./shared";
import { retirementPlan, insuranceNeed, goalPlan } from "../../lib/calc";
import { inr } from "../../lib/format";

function Breakdown({ rows }) {
  const max = Math.max(...rows.map((r) => Math.abs(r[1])), 1);
  return (
    <div className="space-y-3 rounded-2xl bg-slate-50 p-5" data-testid="calc-breakdown">
      {rows.map(([label, v, color]) => (
        <div key={label}>
          <div className="flex justify-between text-xs text-slate-600"><span>{label}</span><span className="font-num font-semibold text-navy">{inr(v)}</span></div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(Math.abs(v) / max) * 100}%`, background: color }} /></div>
        </div>
      ))}
    </div>
  );
}

export function RetirementCalculator() {
  const [v, setV] = useState({ age: 30, retireAge: 60, lifeExpectancy: 85, monthlyExpense: 50000, inflation: 6, preReturn: 11, postReturn: 7, savings: 500000 });
  const s = (k) => (x) => setV((o) => ({ ...o, [k]: x }));
  const res = retirementPlan({ ...v, retireAge: Math.max(v.retireAge, v.age + 1), lifeExpectancy: Math.max(v.lifeExpectancy, v.retireAge + 1) });
  return (
    <CalcLayout title="Retirement Planning Calculator" description="Estimate the corpus needed to fund inflation-adjusted expenses through retirement."
      inputs={<>
        <NumField id="rt-age" label="Current age" suffix="yrs" value={v.age} onChange={s("age")} min={18} max={70} />
        <NumField id="rt-retire" label="Retirement age" suffix="yrs" value={v.retireAge} onChange={s("retireAge")} min={35} max={75} />
        <NumField id="rt-life" label="Life expectancy" suffix="yrs" value={v.lifeExpectancy} onChange={s("lifeExpectancy")} min={60} max={100} />
        <NumField id="rt-expense" label="Current monthly expenses" prefix="₹" value={v.monthlyExpense} onChange={s("monthlyExpense")} min={5000} max={1000000} step={1000} />
        <NumField id="rt-inflation" label="Expected inflation" suffix="%" value={v.inflation} onChange={s("inflation")} min={2} max={12} step={0.5} />
        <NumField id="rt-pre" label="Return before retirement" suffix="%" value={v.preReturn} onChange={s("preReturn")} min={4} max={18} step={0.5} />
        <NumField id="rt-post" label="Return after retirement" suffix="%" value={v.postReturn} onChange={s("postReturn")} min={3} max={12} step={0.5} />
        <NumField id="rt-savings" label="Existing retirement savings" prefix="₹" value={v.savings} onChange={s("savings")} min={0} max={100000000} step={10000} />
      </>}
      results={<>
        <Stat label="Monthly expense at retirement" value={inr(res.monthlyAtRetirement)} testid="rt-result-expense" />
        <Stat label="Corpus required" value={inr(res.corpus)} testid="rt-result-corpus" />
        <Stat label="Monthly SIP needed" value={inr(res.monthlySip)} testid="rt-result-sip" accent />
      </>}
      chart={<Breakdown rows={[["Corpus required", res.corpus, "#071426"], ["Existing savings (future value)", res.savingsFV, "#D6B36A"], ["Shortfall to build", res.shortfall, "#00C987"]]} />} />
  );
}

export function InsuranceCalculator() {
  const [v, setV] = useState({ annualIncome: 1200000, years: 15, loans: 2500000, goals: 2000000, existingCover: 5000000, savings: 500000 });
  const s = (k) => (x) => setV((o) => ({ ...o, [k]: x }));
  const res = insuranceNeed(v);
  return (
    <CalcLayout title="Insurance Coverage Calculator" description="A needs-based estimate of the life cover your family may need."
      inputs={<>
        <NumField id="ins-income" label="Annual income" prefix="₹" value={v.annualIncome} onChange={s("annualIncome")} min={100000} max={50000000} step={10000} />
        <NumField id="ins-years" label="Years of income to replace" suffix="yrs" value={v.years} onChange={s("years")} min={1} max={40} />
        <NumField id="ins-loans" label="Outstanding loans" prefix="₹" value={v.loans} onChange={s("loans")} min={0} max={100000000} step={10000} />
        <NumField id="ins-goals" label="Future goals (education, marriage)" prefix="₹" value={v.goals} onChange={s("goals")} min={0} max={100000000} step={10000} />
        <NumField id="ins-existing" label="Existing life cover" prefix="₹" value={v.existingCover} onChange={s("existingCover")} min={0} max={200000000} step={100000} />
        <NumField id="ins-savings" label="Liquid savings & investments" prefix="₹" value={v.savings} onChange={s("savings")} min={0} max={100000000} step={10000} />
      </>}
      results={<>
        <Stat label="Income replacement" value={inr(res.incomeReplacement)} testid="ins-result-income" />
        <Stat label="Total requirement" value={inr(res.gross)} testid="ins-result-gross" />
        <Stat label="Additional cover needed" value={inr(res.need)} testid="ins-result-need" accent />
      </>}
      chart={<Breakdown rows={[["Total requirement", res.gross, "#071426"], ["Existing cover + savings", v.existingCover + v.savings, "#D6B36A"], ["Additional cover needed", res.need, "#00C987"]]} />}
      note="Indicative estimate only, using a simplified needs-based method. Actual cover depends on insurer underwriting, health, and policy terms. Insurance is the subject matter of solicitation." />
  );
}

export function GoalCalculator() {
  const [v, setV] = useState({ cost: 2500000, years: 10, inflation: 6, returnPct: 11, saved: 200000 });
  const s = (k) => (x) => setV((o) => ({ ...o, [k]: x }));
  const res = goalPlan(v);
  return (
    <CalcLayout title="Goal-Based Investment Calculator" description="Plan for a goal such as a child's education, a home or a car."
      inputs={<>
        <NumField id="goal-cost" label="Goal cost today" prefix="₹" value={v.cost} onChange={s("cost")} min={10000} max={200000000} step={10000} />
        <NumField id="goal-years" label="Years to goal" suffix="yrs" value={v.years} onChange={s("years")} min={1} max={40} />
        <NumField id="goal-inflation" label="Expected inflation" suffix="%" value={v.inflation} onChange={s("inflation")} min={0} max={15} step={0.5} />
        <NumField id="goal-return" label="Expected return" suffix="%" value={v.returnPct} onChange={s("returnPct")} min={1} max={20} step={0.5} />
        <NumField id="goal-saved" label="Already saved for this goal" prefix="₹" value={v.saved} onChange={s("saved")} min={0} max={100000000} step={10000} />
      </>}
      results={<>
        <Stat label="Future cost of goal" value={inr(res.futureCost)} testid="goal-result-future" />
        <Stat label="Lumpsum needed today" value={inr(res.lumpsumToday)} testid="goal-result-lumpsum" />
        <Stat label="Or monthly SIP" value={inr(res.monthlySip)} testid="goal-result-sip" accent />
      </>}
      chart={<Breakdown rows={[["Future cost of goal", res.futureCost, "#071426"], ["Savings grown to goal date", res.savedFV, "#D6B36A"], ["Gap to fund", res.gap, "#00C987"]]} />} />
  );
}
